import React, { useEffect, useState } from 'react';
import * as SituationAPI from '../../../services/SituationServices';
import styles from './styles/ViewSituationPreviewModal.module.css';
import Loading from '../../loading/Loading';
import { transformIframes } from '../../../utils/utils';
import MediaPlayer from '../../../utils/MediaPlayer';

const ViewSituationPreviewModal = ({ situationId, onClose, isTeacher, isOwner }) => {
    const [situation, setSituation] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!situationId) return;

        const fetchSituationPreview = async () => {
            setLoading(true);
            try {
                const data = await SituationAPI.getSituationPreview(situationId);
                setSituation(data);
            } catch (err) {
                console.error("Error fetching situation preview:", err);
                setError("Failed to load situation preview.");
            } finally {
                setLoading(false);
            }
        };

        fetchSituationPreview();
    }, [situationId]);

    const displayableChoices = situation?.choices
        ? (isOwner || isTeacher)
            ? situation.choices
            : situation.choices.filter(choice => !choice.showIfKey && !choice.hideIfKey)
        : [];

    return (
        <div className={styles.modalBackdrop} onClick={onClose}>
            <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
                <button className={styles.closeButton} onClick={onClose}>&times;</button>
                {loading && <Loading />}
                {error && <p className={styles.error}>{error}</p>}
                {situation && (
                    <div className={styles.previewContainer}>
                        <h2 className={styles.mainTitle}>Preview: {situation.title}</h2>

                        {/* GROUP */}
                        {situation.situationGroup && (
                            <div className={styles.groupContainer}>
                                {situation.situationGroup.audio && (
                                    <div className={styles.mediaContainer}>
                                        <MediaPlayer
                                            key={situation.situationGroup.audio}
                                            src={situation.situationGroup.audio}
                                            autoplay={situation.situationGroup.autoplay || false}
                                        />
                                        {situation.situationGroup.audioLabel && (
                                            <div className={styles.mediaLabel}>{situation.situationGroup.audioLabel}</div>
                                        )}
                                    </div>
                                )}
                                {situation.situationGroup.image && (
                                    <div className={styles.imageContainer}>
                                        <img src={situation.situationGroup.image} alt="Situation Group" className={styles.image} />
                                    </div>
                                )}
                                <div
                                    className={styles.text}
                                    dangerouslySetInnerHTML={{ __html: transformIframes(situation.situationGroup.text) }}
                                />
                            </div>
                        )}

                        {/* SITUATION BODY */}
                        <div className={styles.situationContainer}>
                            {situation.audio && (
                                <div className={styles.mediaContainer}>
                                    <MediaPlayer
                                        key={situation.audio}
                                        src={situation.audio}
                                        autoplay={situation.autoplay || false}
                                    />
                                    {situation.audioLabel && (
                                        <div className={styles.mediaLabel}>{situation.audioLabel}</div>
                                    )}
                                </div>
                            )}
                            {situation.image && (
                                <div className={styles.imageContainer}>
                                    <img src={situation.image} alt={situation.title} className={styles.image} />
                                </div>
                            )}
                            <div
                                className={styles.text}
                                dangerouslySetInnerHTML={{ __html: transformIframes(situation.text) }}
                            />
                        </div>

                        {/* CHOICES, READ-ONLY */}
                        {displayableChoices.length > 0 && (
                            <div className={styles.choicesContainer}>
                                {displayableChoices.map(choice => {
                                    const isConditional = choice.showIfKey || choice.hideIfKey;
                                    const canSeeSpecialStyles = isOwner || isTeacher;

                                    let choiceClassName = styles.choicePreview;
                                    if (canSeeSpecialStyles) {
                                        if (isConditional) {
                                            choiceClassName += ` ${styles.conditionalChoice}`;
                                        }
                                        if (choice.correctness === 'correct') {
                                            choiceClassName += ` ${styles.correctChoice}`;
                                        } else if (choice.correctness === 'incorrect') {
                                            choiceClassName += ` ${styles.incorrectChoice}`;
                                        }
                                    }

                                    return (
                                        <div key={choice._id} className={choiceClassName}>
                                            {choice.image && (
                                                <div className={styles.choiceImageContainer}>
                                                    <img src={choice.image} alt="Choice" className={styles.choiceImage} />
                                                </div>
                                            )}
                                            <div
                                                className={styles.choiceText}
                                                dangerouslySetInnerHTML={{ __html: transformIframes(choice.text) }}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ViewSituationPreviewModal;